/*
 * UserMap.ts
 * ===========
 * Map visualization for a user, which appears in the background of Aware Viz.
 */
import * as THREE from "three";
import mapboxgl, { LngLatBounds } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "apple-mapkit-js";
import * as polyline from "@mapbox/polyline";
import { Timestamp } from "tsproto/google/protobuf/timestamp";
import { Event as AwareEvent } from "ts/tsproto/aware_proto/proto/Event";
import { Geolocation } from "tsproto/aware_proto/proto/Location";
import DateUtils from "../../../shared/DateUtils";
import { makeObservable, toJS } from "mobx";
import "@material-design-icons/font/filled.css";
class ASIPlacePopup extends mapboxgl.Popup {
    placeUid;
    constructor(options) {
        super(options);
    }
}
export default class UserMap {
    static Uid = "Map";
    app;
    group;
    position = new THREE.Vector3();
    mapboxMap;
    mapkitMap;
    loadPromise;
    mapContainer;
    mapDiv;
    markers = {};
    placesData;
    unitsPerHour = 0.05;
    // Mapbox items
    focus_time_position_marker;
    trajectory_markers;
    latest_timeline;
    latest_statistics;
    // Currently displayed markers for a places's route statistics
    route_statistics_markers = [];
    // List of markers for place statistics
    placeStatMarkers = {};
    selected_place_stat_marker;
    constructor(app) {
        this.app = app;
        this.onPopupOpen = this.onPopupOpen.bind(this);
        this.onPopupClose = this.onPopupClose.bind(this);
        makeObservable(this);
        // this.initMapboxgl();
    }
    initMapboxgl() {
        if (this.loadPromise)
            return this.loadPromise;
        this.loadPromise = new Promise((resolve) => {
            const map = new mapboxgl.Map({
                accessToken: "pk.eyJ1IjoiY3J5c3RhbGNoYW8tYXNpIiwiYSI6ImNsNDkxdGdsMjB4dm0zbG40aHJjeHE1c20ifQ.tc5F9jF2JP-sVhFKZjlaOQ",
                // style: "mapbox://styles/mapbox/dark-v10",
                style: "mapbox://styles/crystalchao-asi/cl49201k9000016mk6c73wxma",
                center: [-122.3923, 37.7857],
                zoom: 15.5,
                pitch: 45,
                bearing: -17.6,
                container: document.getElementById("map"),
                antialias: true,
            });
            // map.addControl(new mapboxgl.NavigationControl());
            // Because of the CSS context being scaled 1000x, the default resize implementation causes large
            // numbers to be set for the canvas dimensions that lead to freezing. This function override
            // does basically the same thing as the original, but uses the correct container width and height.
            // See issue: https://github.com/mapbox/mapbox-gl-js/issues/3265
            // Original resize(): https://github.com/mapbox/mapbox-gl-js/blob/v0.42.2/src/ui/map.js#L395
            map.resize = ((eventData) => {
                const width = map.getContainer().clientWidth;
                const height = map.getContainer().clientHeight;
                // Replaces _resizeCanvas
                const pixelRatio = window.devicePixelRatio || 1;
                map.getCanvas().width = pixelRatio * width;
                map.getCanvas().height = pixelRatio * height;
                map.getCanvas().style.width = `${width}px`;
                map.getCanvas().style.height = `${height}px`;
                map["transform"]?.resize(width, height);
                map["painter"]?.resize(width, height);
                return map
                    .fire("movestart")
                    .fire("move")
                    .fire("resize")
                    .fire("moveend");
            }).bind(map);
            map.once("load", () => {
                this.initializeRouteLayer(map);
                this.initializePlaceLayer(map);
                map.resize();
                resolve();
            });
            this.mapboxMap = map;
        });
        return this.loadPromise;
    }
    initializePlaceLayer(map) {
        const sourceId = "places-source";
        map.addSource(sourceId, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: []
            }
        });
        const layerId = "places-layer";
        map.addLayer({
            id: layerId,
            type: "symbol",
            source: sourceId,
            layout: {
                "text-field": "{title}",
                "text-size": 18,
                "text-anchor": "top",
                "text-allow-overlap": false,
                "text-offset": [0, 0.5]
            },
            paint: {
                // "text-color": "cyan", //["get", "color"],
                "text-color": [
                    "case",
                    ["==", ["get", "highlighted"], true],
                    // ["boolean", ["feature-state", "highlighted"], false],
                    "rgb(255, 130, 126)",
                    "cyan",
                ],
                "text-opacity": 1, //["get", "opacity"],
            },
        });
    }
    initializeRouteLayer(map) {
        // Place the layers behind the first symbol, so text renders above the route
        let firstSymbolId;
        const layers = map.getStyle().layers;
        for (const layer of layers) {
            if (layer.type === "symbol") {
                firstSymbolId = layer.id;
                break;
            }
        }
        // Source and layer for user route
        const routeSourceId = "route-source";
        map.addSource(routeSourceId, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: []
            }
        });
        const routeColor = "#00ffff";
        const glowLayers = [
            { id: "route-glow-1", color: routeColor, width: 10, blur: 15, opacity: 1 },
            { id: "route-glow-2", color: routeColor, width: 15, blur: 20, opacity: 1 },
            { id: "route-glow-3", color: routeColor, width: 20, blur: 30, opacity: 1 },
        ];
        glowLayers.forEach(layer => {
            map.addLayer({
                id: layer.id,
                type: "line",
                source: routeSourceId,
                layout: {
                    "line-join": "round",
                    "line-cap": "round"
                },
                paint: {
                    "line-color": layer.color,
                    "line-width": layer.width,
                    "line-blur": layer.blur,
                    "line-opacity": layer.opacity
                }
            }, firstSymbolId);
        });
        const routeLayerId = "route-layer";
        map.addLayer({
            id: routeLayerId,
            type: "line",
            source: routeSourceId,
            layout: {
                "line-join": "round",
                "line-cap": "round"
            },
            paint: {
                "line-color": routeColor,
                "line-width": 5,
                "line-opacity": 0.8,
            }
        }, firstSymbolId);
    }
    displayExampleEvent() {
        const example1 = AwareEvent.create();
        example1.endDate = Timestamp.fromDate(new Date());
        const now_time = new Date();
        const prev_time = new Date(now_time.valueOf() - 60.0 * 60000);
        const future_time = new Date(now_time.valueOf() + 60.0 * 60000);
        example1.startDate = Timestamp.fromDate(prev_time);
        example1.endDate = Timestamp.fromDate(future_time);
        const event1_loc = Geolocation.create();
        event1_loc.latitude = 37.77926;
        event1_loc.longitude = -122.41925;
        example1.geolocation.push(event1_loc);
        example1.eventName = "Example Event";
        console.log(example1);
        this.displayEvent(example1);
    }
    displayExampleTravelEvent() {
        const example1 = AwareEvent.create();
        example1.endDate = Timestamp.fromDate(new Date());
        const now_time = new Date();
        const prev_time = new Date(now_time.valueOf() - 60.0 * 60000);
        const future_time = new Date(now_time.valueOf() + 60.0 * 60000);
        example1.startDate = Timestamp.fromDate(prev_time);
        example1.endDate = Timestamp.fromDate(future_time);
        const event1_loc1 = Geolocation.create();
        event1_loc1.latitude = 37.7857;
        event1_loc1.longitude = -122.3923;
        example1.geolocation.push(event1_loc1);
        const event1_loc2 = Geolocation.create();
        event1_loc2.latitude = 37.77926;
        event1_loc2.longitude = -122.41925;
        example1.geolocation.push(event1_loc2);
        example1.eventName = "Example Event";
        console.log(example1);
        this.displayEvent(example1);
    }
    geoJsonFromTimeline(timeline) {
        const geojson_route = {
            "type": "geojson",
            "data": {
                "type": "Feature",
                "properties": {},
                "geometry": {
                    "type": "LineString",
                    "coordinates": []
                }
            }
        };
        timeline.geolocations.forEach(loc => {
            geojson_route.data.geometry.coordinates.push([loc.longitude, loc.latitude]);
        });
        return geojson_route;
    }
    displayLine(g1, g2) {
        console.log("Line start: %f %f", g1.longitude, g1.latitude);
        console.log("Line end: %f %f", g2.longitude, g2.latitude);
        const geojson_route = {
            "type": "geojson",
            "data": {
                "type": "Feature",
                "properties": {},
                "geometry": {
                    "type": "LineString",
                    "coordinates": []
                }
            }
        };
        geojson_route.data.geometry.coordinates.push([g1.longitude, g1.latitude]);
        geojson_route.data.geometry.coordinates.push([g2.longitude, g2.latitude]);
        return geojson_route;
    }
    geoJsonFromTimelineForEvent(timeline, event_uid) {
        const geojson_route = {
            "type": "geojson",
            "data": {
                "type": "Feature",
                "properties": {},
                "geometry": {
                    "type": "LineString",
                    "coordinates": []
                }
            }
        };
        let target_idx = 0;
        for (let event_idx = 0; event_idx < this.latest_timeline.events.length; event_idx++) {
            if (this.latest_timeline.events[event_idx].eventUid == event_uid) {
                target_idx = event_idx;
                break;
            }
        }
        const start_date = this.latest_timeline.events[target_idx].startDate;
        const end_date = this.latest_timeline.events[target_idx].endDate;
        const start_idx = this.indexForTimelineStamp(this.latest_timeline, Timestamp.toDate(start_date));
        const end_idx = this.indexForTimelineStamp(this.latest_timeline, Timestamp.toDate(end_date));
        for (let i = start_idx; i < end_idx; i++) {
            geojson_route.data.geometry.coordinates.push([timeline.geolocations[i].longitude, timeline.geolocations[i].latitude]);
        }
        // timeline.geolocations.forEach(loc => {
        //   geojson_route.data.geometry.coordinates.push([loc.longitude, loc.latitude]);
        // });
        return geojson_route;
    }
    indexForTimelineStamp(timeline, target_date) {
        let index = 0;
        const diff = target_date.valueOf() - Timestamp.toDate(timeline.timelineInformation.startTime).valueOf();
        const temporal_res = timeline.timelineInformation.temporalResolution * 1000;
        index = Math.round(diff / temporal_res);
        if ((index < 0) || (index >= timeline.geolocations.length)) {
            return null;
        }
        return index;
    }
    getLocationForTime(timeline, target_date) {
        const index = this.indexForTimelineStamp(timeline, target_date);
        if (index == null) {
            return null;
        }
        return timeline.geolocations[index];
    }
    displayTimeline(timeline) {
        console.log("css3DMap: display timeline.");
        this.latest_timeline = timeline;
        if ((this.focus_time_position_marker == null) && (timeline.geolocations.length > 0)) {
            this.focus_time_position_marker = new mapboxgl.Marker().setLngLat([timeline.geolocations[0].longitude, timeline.geolocations[0].latitude]).addTo(this.mapboxMap);
        }
        const geojson_route = this.geoJsonFromTimeline(timeline);
        this.mapboxMap.addSource("route", geojson_route);
        this.mapboxMap.addLayer({
            "id": "route",
            "type": "line",
            "source": "route",
            "layout": {
                "line-join": "round",
                "line-cap": "round"
            },
            "paint": {
                "line-color": "#888",
                "line-width": 8
            }
        });
    }
    moveCameraToEventFromTimeline(event_uid, timeline) {
        console.log("Moving camera to event");
    }
    displayEventFromTimeline(event_uid, timeline) {
        console.log("css3DMap: display event from timeline.");
        this.latest_timeline = timeline;
        if ((this.focus_time_position_marker == null) && (timeline.geolocations.length > 0)) {
            this.focus_time_position_marker = new mapboxgl.Marker({ color: "red" }).setLngLat([timeline.geolocations[0].longitude, timeline.geolocations[0].latitude]).addTo(this.mapboxMap);
        }
        const geojson_route = this.geoJsonFromTimelineForEvent(timeline, event_uid);
        this.mapboxMap.addSource("route", geojson_route);
        this.mapboxMap.addLayer({
            "id": "route",
            "type": "line",
            "source": "route",
            "layout": {
                "line-join": "round",
                "line-cap": "round"
            },
            "paint": {
                "line-color": "#888",
                "line-width": 8
            }
        });
    }
    clearRouteEvent() {
        if (this.mapboxMap.getLayer("route")) {
            this.mapboxMap.removeLayer("route");
        }
        if (this.mapboxMap.getSource("route")) {
            this.mapboxMap.removeSource("route");
        }
    }
    displayEventUidTrajectory(event_uid) {
        if (this.latest_timeline == null) {
            console.log("No timeline, unable to show event uid in map.");
            return;
        }
        let target_idx = 0;
        for (let event_idx = 0; event_idx < this.latest_timeline.events.length; event_idx++) {
            if (this.latest_timeline.events[event_idx].eventUid == event_uid) {
                target_idx = event_idx;
                break;
            }
        }
        const start_date = this.latest_timeline.events[target_idx].startDate;
        const end_date = this.latest_timeline.events[target_idx].endDate;
        const start_idx = this.indexForTimelineStamp(this.latest_timeline, Timestamp.toDate(start_date));
        const end_idx = this.indexForTimelineStamp(this.latest_timeline, Timestamp.toDate(end_date));
        for (let loc_idx = start_idx; loc_idx < end_idx; loc_idx++) {
            const marker1 = new mapboxgl.Marker().setLngLat([this.latest_timeline.geolocations[loc_idx].longitude, this.latest_timeline.geolocations[loc_idx].latitude]).addTo(this.mapboxMap);
            this.trajectory_markers.push(marker1);
        }
    }
    clearEventUidTrajectory() {
    }
    displayEvent(event) {
        if (event.geolocation.length < 1) {
            return;
        }
        if (!this.mapboxMap) {
            return;
        }
        for (let loc_idx = 0; loc_idx < event.geolocation.length; loc_idx++) {
            const marker1 = new mapboxgl.Marker().setLngLat([event.geolocation[loc_idx].longitude, event.geolocation[loc_idx].latitude]).addTo(this.mapboxMap);
            // this.mapboxMap.flyTo({
            //   center: [event.geolocation[0].longitude, event.geolocation[0].latitude],
            //   essential: true
            //   });
        }
    }
    displayPlace(place) {
        console.log("Adding place to map: %s \n %f,%f", place.displayNames[0].name, place.geolocationRegion.center.latitude, place.geolocationRegion.center.longitude);
        const marker = new mapboxgl.Marker().setLngLat([place.geolocationRegion.center.longitude, place.geolocationRegion.center.latitude]).addTo(this.mapboxMap);
    }
    onPopupOpen(e) {
        const popup = e.target;
        console.log("Popup opened for ", popup.placeUid);
        const marker_location = popup.getLngLat();
        // Find routes
        this.clearAllRouteStats();
        for (let i = 0; i < this.latest_statistics.movementStatistics.length; i++) {
            let p0_match = false;
            let p1_match = false;
            p0_match = this.latest_statistics.movementStatistics[i].places[0].placeUid == popup.placeUid;
            p1_match = this.latest_statistics.movementStatistics[i].places[1].placeUid == popup.placeUid;
            if (p0_match || p1_match) {
                this.displayRouteStats(this.latest_statistics.movementStatistics[i]);
            }
        }
        // console.log(this.mapboxMap);
        // this.mapboxMap.flyTo({
        //   center: marker_location,
        //   zoom: 16,
        //   duration: 2000,
        //   //speed:1.2,
        //   curve:1
        // });
    }
    updatePlaceLayerSourceData(places) {
        const placesData = {
            type: "FeatureCollection",
            features: []
        };
        for (const place of places) {
            const center = place.geolocationRegion?.center;
            if (!center) {
                console.warn(`No center for place ${place.placeUid}`);
                continue;
            }
            placesData.features.push({
                type: "Feature",
                id: place.placeUid,
                geometry: {
                    type: "Point",
                    coordinates: [center.longitude, center.latitude],
                },
                properties: {
                    title: place.displayNames[0].name,
                    highlighted: false
                    // opacity: 1
                },
            });
        }
        const placesSource = this.mapboxMap.getSource("places-source");
        placesSource?.setData(placesData);
        this.placesData = placesData;
    }
    clearPlaceLayerSourceData() {
        const placesData = {
            type: "FeatureCollection",
            features: []
        };
        const placesSource = this.mapboxMap.getSource("places-source");
        placesSource?.setData(placesData);
        this.placesData = placesData;
    }
    createPlaceMarker(place, showNames) {
        let iconName = "";
        const category = place.placeCategory.toLowerCase();
        if (category === "restaurant") {
            iconName = "restaurant";
        }
        else if (category === "bar") {
            iconName = "local_bar";
        }
        else if (category === "cafe") {
            iconName = "local_cafe";
        }
        const name = showNames ? place.displayNames[0].name : "";
        if (!place.placeUid) {
            console.warn("Marker ID not provided!");
            if (process.env.NODE_ENV === "development") {
                console.log(toJS(place));
            }
        }
        return this.createMarker(place.placeUid, name, iconName);
    }
    createMarker(markerId, text, iconName) {
        const el = document.createElement("div");
        el.className = "marker-container";
        const span = document.createElement("div");
        span.className = "material-icons";
        span.innerHTML = iconName || "adjust";
        Object.assign(span.style, {
            background: "rgba(0, 255, 255, 0.8)",
            color: "rgb(16, 16, 16)",
            border: "1px solid white",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            fontSize: "14px",
            textAlign: "center",
            paddingTop: "2px",
        });
        el.appendChild(span);
        const labelEl = document.createElement("div");
        labelEl.className = "marker-label";
        labelEl.innerText = text;
        el.appendChild(labelEl);
        Object.assign(labelEl.style, {
            position: "absolute",
            top: "10px",
            marginTop: "10px",
            marginLeft: "10px",
            width: "150px",
            textAlign: "center",
            transform: "translateX(-50%)",
            color: "cyan",
            fontSize: "18px",
        });
        const marker = new mapboxgl.Marker(el);
        if (markerId in this.markers) {
            // this.markers[markerId].remove();
            console.warn(`Marker ${markerId} already exists!`);
        }
        this.markers[markerId] = marker;
        return marker;
    }
    setHighlighted(id, highlighted) {
        const marker = this.markers[id];
        if (marker) {
            for (let i = 0; i < marker.getElement().children.length; ++i) {
                const child = marker.getElement().children[i];
                child.style.background = highlighted ?
                    "rgba(255, 130, 126, 0.8)" : "rgba(0, 255, 255, 0.8)";
            }
        }
        // this.mapboxMap.setFeatureState(
        //   { source: "places-source", id: id },
        //   { highlighted: highlighted }
        // );
        // const state = this.mapboxMap.getFeatureState({ source: "places-source", id: id });
        // console.log(state);
        const feature = this.placesData.features.find(f => f.id === id);
        if (feature) {
            feature.properties.highlighted = highlighted;
            const placesSource = this.mapboxMap.getSource("places-source");
            placesSource?.setData(this.placesData);
        }
    }
    showMarkerSet(places, showNames) {
        this.clearMarkers();
        const allCoords = [];
        for (const place of places) {
            const center = place.geolocationRegion.center;
            const coords = [center.longitude, center.latitude];
            this.createPlaceMarker(place, showNames).setLngLat(coords).addTo(this.mapboxMap);
            allCoords.push(coords);
        }
        this.fitBounds(allCoords);
    }
    clearMarkers() {
        Object.values(this.markers).forEach(marker => marker.remove());
        this.markers = {};
    }
    showEvent(event) {
        if (!(event?.geolocation?.length > 0))
            return;
        if (event.geolocation.length === 1) {
            this.clearMarkers();
            const geolocation = event.geolocation?.[0];
            const coords = [geolocation.longitude, geolocation.latitude];
            this.flyTo(coords);
            this.createMarker(event.eventUid, event.eventName).setLngLat(coords).addTo(this.mapboxMap);
        }
        else if (event.geolocation.length === 2) {
            this.clearMarkers();
            const allCoords = [];
            for (const geolocation of event.geolocation) {
                const coords = [geolocation.longitude, geolocation.latitude];
                allCoords.push(coords);
                new mapboxgl.Marker().setLngLat(coords).addTo(this.mapboxMap);
            }
            this.fitBounds(allCoords);
        }
        if (event.routePolyline) {
            this.showRoute(event.routePolyline);
        }
        else {
            this.hideRoute();
        }
    }
    flyTo(coordinates) {
        const width = document.documentElement.clientWidth;
        const height = document.documentElement.clientHeight;
        const xOffset = 0.25 * width;
        const yOffset = this.app?.statsChart.isVisible() ? -0.175 * height : 0;
        this.mapboxMap.flyTo({
            center: coordinates,
            zoom: 16,
            offset: [xOffset, yOffset],
            duration: 2000,
            essential: true
        });
    }
    fitBounds(coordinates) {
        if (!(coordinates?.length > 0))
            return;
        const width = document.documentElement.clientWidth;
        const height = document.documentElement.clientHeight;
        const xOffset = 0.5 * width;
        const yOffset = this.app?.statsChart.isVisible() ? 0.35 * height : 0;
        const bounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]);
        for (const coord of coordinates) {
            bounds.extend(coord);
        }
        const padding = 250;
        this.mapboxMap.fitBounds(bounds, {
            padding: {
                top: padding,
                right: padding,
                bottom: yOffset + padding,
                left: xOffset + padding
            },
            maxZoom: 16
        });
    }
    hideRoute() {
        this.mapboxMap.setLayoutProperty("route-layer", "visibility", "none");
        this.mapboxMap.setLayoutProperty("route-glow-1", "visibility", "none");
        this.mapboxMap.setLayoutProperty("route-glow-2", "visibility", "none");
        this.mapboxMap.setLayoutProperty("route-glow-3", "visibility", "none");
    }
    showRoute(polylineString) {
        const geoJson = polyline.toGeoJSON(polylineString, 6);
        if (!geoJson) {
            console.warn("A valid polyline string was not provided: " + polylineString);
            return;
        }
        const routeData = {
            type: "FeatureCollection",
            features: []
        };
        routeData.features = [{
                type: "Feature",
                geometry: {
                    type: "LineString",
                    coordinates: geoJson.coordinates,
                },
                properties: {}
            }];
        const routeSource = this.mapboxMap.getSource("route-source");
        routeSource?.setData(routeData);
        this.mapboxMap.setLayoutProperty("route-layer", "visibility", "visible");
        this.mapboxMap.setLayoutProperty("route-glow-1", "visibility", "visible");
        this.mapboxMap.setLayoutProperty("route-glow-2", "visibility", "visible");
        this.mapboxMap.setLayoutProperty("route-glow-3", "visibility", "visible");
        this.fitBounds(geoJson.coordinates);
    }
    onPopupClose(e) {
        const popup = e.target;
        console.log("Popup closed for ", popup.placeUid);
        this.clearAllRouteStats();
    }
    openPlaceStatMarkerOnFlyToEnd() {
        console.log("openPlaceStatMarkerOnFlyToEnd");
        if (this.selected_place_stat_marker != null) {
            console.log("has a marker!");
            //this.selected_place_stat_marker.addTo(this.mapboxMap);
            this.selected_place_stat_marker.togglePopup();
        }
        else {
            console.log("no marker!");
        }
    }
    flyToPlaceStats(place_uid) {
        if (this.selected_place_stat_marker != null) {
            this.selected_place_stat_marker.getPopup().remove();
            this.selected_place_stat_marker = null;
        }
        if (this.placeStatMarkers[place_uid] != null) {
            console.log("Has place uid");
            const place_marker = this.placeStatMarkers[place_uid];
            //place_marker.getPopup().addTo(this.mapboxMap);
            this.selected_place_stat_marker = place_marker;
            this.mapboxMap.flyTo({
                center: place_marker.getLngLat(),
                zoom: 16,
                duration: 2000,
                //speed:1.2,
                curve: 1
            });
            this.mapboxMap.once("moveend", () => { this.openPlaceStatMarkerOnFlyToEnd(); });
        }
        else {
            console.log("does not have.");
        }
        // for (let i = 0; i < this.latest_statistics.visitStatistics.length; i++) {
        //   if (place_uid == this.latest_statistics.visitStatistics[i].places[0].placeUid)
        //   {
        //     const marker_lnglat = new mapboxgl.LngLat(this.latest_statistics.visitStatistics[i].places[0].geolocationRegion.center.longitude, this.latest_statistics.visitStatistics[i].places[0].geolocationRegion.center.latitude);
        //     this.mapboxMap.flyTo({
        //       center: marker_lnglat,
        //       zoom: 16,
        //       duration: 2000,
        //       //speed:1.2,
        //       curve:1
        //     });
        //   }
        // }
    }
    displayPlaceStats(place_stats) {
        console.log("Adding place to map: %s \n %f,%f", place_stats.places[0].displayNames[0].name, place_stats.places[0].geolocationRegion.center.latitude, place_stats.places[0].geolocationRegion.center.longitude);
        let display_str = "<h3>" + place_stats.places[0].displayNames[0].name + "</h3><p>Total Visits: " + place_stats.allEvents.length + "</p>"; //"<h3>Hello World!</h3><p>This is a popup on click.</p>"
        //display_str += "<p>Avg Visit:" + place_stats.durationMean.toFixed(0) + " minutes.</p><p></p>";
        display_str += "<p>Additional Names:</p>";
        for (let i = 0; i < place_stats.places[0].displayNames.length; i++) {
            display_str += "<p>" + place_stats.places[0].displayNames[i].name + "</p>";
        }
        display_str += "<p>Avg Visit: " + DateUtils.getDurationString(place_stats.durationMeanMinutes) + " +/- " + DateUtils.getDurationString(place_stats.durationStddevMinutes) + ".</p><p></p>";
        for (let i = 0; i < place_stats.allEvents.length; i++) {
            //display_str += "<p>" + Timestamp.toDate(place_stats.allEvents[i].startDate).toISOString() + "<br>" + Timestamp.toDate(place_stats.allEvents[i].endDate).toISOString() + "</p>";
            display_str += "<p>" + DateUtils.getDateTimeString(Timestamp.toDate(place_stats.allEvents[i].startDate)) + "<br>" + DateUtils.getDateTimeString(Timestamp.toDate(place_stats.allEvents[i].endDate)) + "</p>";
        }
        // const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(
        //   display_str
        // );
        const popup = new ASIPlacePopup({ offset: 25 }).setHTML(display_str);
        popup.placeUid = place_stats.places[0].placeUid;
        popup.on("open", this.onPopupOpen);
        popup.on("close", this.onPopupClose);
        const marker = new mapboxgl.Marker().setLngLat([place_stats.places[0].geolocationRegion.center.longitude, place_stats.places[0].geolocationRegion.center.latitude]).setPopup(popup).addTo(this.mapboxMap);
        this.placeStatMarkers[place_stats.places[0].placeUid] = marker;
    }
    displayRouteStats(route_stats) {
        this.clearAllRouteStats();
        console.log("Adding route with places %d", route_stats.places.length);
        //let geojson_route = this.displayLine(route_stats.places[0].geolocationRegion.center, route_stats.places[1].geolocationRegion.center);
        const geojson_route = polyline.toGeoJSON(route_stats.routePolyline, 6);
        const route_name = "route-" + route_stats.places[0].placeUid + "-" + route_stats.places[1].placeUid;
        this.route_statistics_markers.push(route_name);
        // Check if a source with this name already exists to prevent errors
        if (this.mapboxMap.getSource(route_name)) {
            // If it exists, just update the data
            this.mapboxMap.getSource(route_name).setData(geojson_route);
        }
        else {
            // 👇 **THE FIX IS HERE** 👇
            // Wrap the GeoJSON data in a source object
            this.mapboxMap.addSource(route_name, {
                "type": "geojson",
                "data": geojson_route // Pass the geometry as the 'data'
            });
            this.mapboxMap.addLayer({
                "id": route_name,
                "type": "line",
                "source": route_name,
                "layout": {
                    "line-join": "round",
                    "line-cap": "round"
                },
                "paint": {
                    "line-color": "#888",
                    "line-width": 8
                }
            });
            const coordinates = geojson_route.coordinates;
            const bounds = new LngLatBounds(coordinates[0], coordinates[0]);
            for (const coord of coordinates) {
                bounds.extend(coord);
            }
            // Animate the map to fit the new bounds
            this.mapboxMap.fitBounds(bounds, {
                padding: 100 // Adds a 100px buffer around the bounds
            });
        }
    }
    clearAllRouteStats() {
        console.log("clearing route stats");
        console.log(this.route_statistics_markers.length);
        let next_item = this.route_statistics_markers.pop();
        while (next_item != undefined) {
            this.mapboxMap.removeLayer(next_item);
            this.mapboxMap.removeSource(next_item);
            next_item = this.route_statistics_markers.pop();
        }
        // for (let i = 0; i < this.route_statistics_markers.length; i++)
        // {
        //   this.mapboxMap.removeLayer(this.route_statistics_markers[i]);
        //   this.mapboxMap.removeSource(this.route_statistics_markers[i]);
        // }
    }
}
